import { useState, useCallback, useEffect } from 'react';
import { translateWithYoudao, initYoudaoTranslate } from '@/lib/youdao-translate';
import { lookupVocabulary } from '@/lib/vocabulary-lookup-client';
import { vocabularyLevelChecker } from '@/lib/vocabularyLevel';

interface YoudaoTranslation {
  translation?: string;
  phonetic?: string;
  usphone?: string;
  explains?: string[];
}

interface UseYoudaoTranslationReturn {
  translations: Record<string, YoudaoTranslation>;
  loading: Record<string, boolean>;
  getTranslation: (word: string) => Promise<void>;
  isInitialized: boolean;
  initializeService: (appKey: string, appSecret: string) => void;
}

export function useYoudaoTranslation(autoInitialize: boolean = true): UseYoudaoTranslationReturn {
  const [translations, setTranslations] = useState<Record<string, YoudaoTranslation>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [isInitialized, setIsInitialized] = useState(false);

  // 自动初始化服务（可选）
  useEffect(() => {
    if (autoInitialize && !isInitialized) {
      try {
        initYoudaoTranslate();
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize Youdao service:', error);
      }
    }
  }, [autoInitialize, isInitialized]);

  // 内存优化：定期清理过大的状态对象
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setTranslations(prev => {
        const entries = Object.entries(prev);
        if (entries.length > 200) {
          const recent = entries.slice(-100); // 保留最近100个
          return Object.fromEntries(recent);
        }
        return prev;
      });

      setLoading(prev => {
        const activeLoading = Object.entries(prev).filter(([, isLoading]) => isLoading);
        if (Object.keys(prev).length > activeLoading.length) {
          return Object.fromEntries(activeLoading);
        }
        return prev;
      });
    }, 60000); // 每分钟清理一次

    return () => clearInterval(cleanupInterval);
  }, []);

  const initializeService = useCallback((appKey: string, appSecret: string) => {
    try {
      initYoudaoTranslate(appKey, appSecret);
      setIsInitialized(true);
      console.log('Youdao translation service initialized');
    } catch (error) {
      console.error('Failed to initialize Youdao service:', error);
      setIsInitialized(false);
    }
  }, []);

  const getTranslation = useCallback(async (word: string) => {
    const cleanedWord = (word ?? '').trim();
    if (!cleanedWord) {
      return;
    }

    if (translations[cleanedWord]) {
      return;
    }

    setLoading(prev => ({ ...prev, [cleanedWord]: true }));

    try {
      if (vocabularyLevelChecker.hasTranslation(cleanedWord)) {
        const localTranslations = vocabularyLevelChecker.getTranslation(cleanedWord);
        const localized = (() => {
          if (Array.isArray(localTranslations)) {
            return localTranslations.join('; ');
          }
          if (typeof localTranslations === 'string') {
            return localTranslations;
          }
          if (localTranslations != null) {
            return String(localTranslations);
          }
          return '';
        })().trim();
        const localUsphone = vocabularyLevelChecker.getUSPronunciation(cleanedWord) ?? undefined;

        if (localized) {
          setTranslations(prev => ({
            ...prev,
            [cleanedWord]: {
              translation: localized,
              usphone: localUsphone,
            },
          }));
          setLoading(prev => ({ ...prev, [cleanedWord]: false }));
          return;
        }
      }

      const lookupResult = await lookupVocabulary(cleanedWord);
      if (lookupResult) {
        const normalizedTranslation = [lookupResult.translation, lookupResult.definition]
          .map((value) => (typeof value === 'string' ? value.trim() : ''))
          .find((value) => value.length > 0);

        if (normalizedTranslation) {
          const phonetic = typeof lookupResult.phonetic === 'string'
            ? lookupResult.phonetic.trim() || undefined
            : undefined;

          setTranslations(prev => ({
            ...prev,
            [cleanedWord]: {
              translation: normalizedTranslation,
              phonetic,
            },
          }));
          setLoading(prev => ({ ...prev, [cleanedWord]: false }));
          return;
        }
      }
    } catch (lookupError) {
      console.warn('Vocabulary lookup failed, falling back to Youdao:', lookupError);
    }

    if (!isInitialized) {
      console.warn('Youdao service not initialized');
      setLoading(prev => ({ ...prev, [cleanedWord]: false }));
      return;
    }

    try {
      const result = await translateWithYoudao(cleanedWord);
      
      if (result.translation || result.explains) {
        setTranslations(prev => ({
          ...prev,
          [cleanedWord]: {
            translation: result.translation || result.explains?.[0],
            phonetic: result.phonetic,
            usphone: result.usphone,
            explains: result.explains
          }
        }));
      } else {
        // 如果没有翻译结果，设置一个空对象避免重复请求
        setTranslations(prev => ({
          ...prev,
          [cleanedWord]: {
            translation: cleanedWord // 显示原词
          }
        }));
      }
    } catch (error) {
      console.error('Translation error:', error);
      setTranslations(prev => ({
        ...prev,
        [cleanedWord]: {
          translation: cleanedWord
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [cleanedWord]: false }));
    }
  }, [isInitialized, translations]);

  return {
    translations,
    loading,
    getTranslation,
    isInitialized,
    initializeService
  };
}
